import { z } from 'zod';
import { isValidCpf, onlyDigits } from './documents';
import { patchOf } from './patch';

/**
 * Agenda e agendamento online: o dono da loja define os horários de trabalho e os dias de folga,
 * e quem visita a loja escolhe um serviço, o dia e (quando o serviço é por horário) a hora.
 */

/** SLOT: vários atendimentos por dia, cada um com a duração do serviço. DAY: o dia todo para um cliente. */
export const BOOKING_MODES = ['SLOT', 'DAY'] as const;
export type BookingMode = (typeof BOOKING_MODES)[number];

/**
 * AWAITING_PAYMENT: marcado na loja com pagamento na hora; o horário fica guardado até o Pix vencer.
 * PENDING: esperando o dono confirmar; o horário fica guardado até ele confirmar ou recusar.
 */
export const BOOKING_STATUSES = ['AWAITING_PAYMENT', 'PENDING', 'CONFIRMED', 'COMPLETED', 'NO_SHOW', 'CANCELED'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/** ON_SITE: o cliente paga no atendimento. ONLINE: paga (Pix) ao marcar, e o horário só vale pago. */
export const BOOKING_PAYMENTS = ['ON_SITE', 'ONLINE'] as const;
export type BookingPayment = (typeof BOOKING_PAYMENTS)[number];

/** AUTO: o agendamento feito na loja já sai confirmado. MANUAL: o dono confirma cada um. */
export const BOOKING_APPROVALS = ['AUTO', 'MANUAL'] as const;
export type BookingApproval = (typeof BOOKING_APPROVALS)[number];

export const BOOKING_PAYMENT_LABEL: Record<BookingPayment, string> = {
  ON_SITE: 'No atendimento',
  ONLINE: 'Na hora de agendar (Pix)',
};

export const BOOKING_APPROVAL_LABEL: Record<BookingApproval, string> = {
  AUTO: 'Confirmar automaticamente',
  MANUAL: 'Eu confirmo cada agendamento',
};

/** Toda a agenda roda no horário de Brasília (sem horário de verão desde 2019). */
export const BOOKING_TIMEZONE = 'America/Sao_Paulo';

export const BOOKING_WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const;

const TIME = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;
export const timeSchema = z.string().regex(TIME, 'Use o formato HH:MM.');

/** "09:30" → 570. */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h! * 60 + m!;
}

/** 570 → "09:30". */
export function minutesToTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function isRealDate(value: string) {
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/** Dia local AAAA-MM-DD. */
export const localDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato AAAA-MM-DD.')
  .refine(isRealDate, 'Data inválida.');

/** Mês AAAA-MM. */
export const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use o formato AAAA-MM.');

export const workIntervalSchema = z
  .object({ start: timeSchema, end: timeSchema })
  .refine((i) => timeToMinutes(i.start) < timeToMinutes(i.end), { message: 'O fim precisa ser depois do início.', path: ['end'] });
export type WorkInterval = z.infer<typeof workIntervalSchema>;

/** Sete listas de intervalos de trabalho, de domingo (0) a sábado (6). Lista vazia = folga. */
export const weeklyHoursSchema = z
  .array(z.array(workIntervalSchema).max(6))
  .length(7)
  .superRefine((days, ctx) => {
    days.forEach((intervals, day) => {
      const sorted = [...intervals].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
      for (let i = 1; i < sorted.length; i++) {
        if (timeToMinutes(sorted[i]!.start) < timeToMinutes(sorted[i - 1]!.end)) {
          ctx.addIssue({ code: 'custom', message: `Os horários de ${BOOKING_WEEKDAYS[day]} se sobrepõem.`, path: [day] });
          return;
        }
      }
    });
  });
export type WeeklyHours = z.infer<typeof weeklyHoursSchema>;

const businessDay: WorkInterval[] = [
  { start: '09:00', end: '12:00' },
  { start: '13:00', end: '18:00' },
];
/** Padrão de quem liga a agenda: segunda a sexta, 9h às 18h com almoço. */
export const DEFAULT_WEEKLY_HOURS: WeeklyHours = [[], businessDay, businessDay, businessDay, businessDay, businessDay, []];

export const bookingSettingsSchema = z.object({
  /** O dono da loja escolheu oferecer agendamento online. */
  enabled: z.boolean(),
  weeklyHours: weeklyHoursSchema,
  /** De quanto em quanto tempo começa um horário (serviços por horário). */
  slotStepMinutes: z.coerce.number().int().min(5, 'Mínimo de 5 minutos.').max(240, 'Máximo de 4 horas.'),
  /** Antecedência mínima para o cliente marcar. */
  minNoticeMinutes: z.coerce.number().int().min(0).max(30 * 24 * 60, 'Máximo de 30 dias.'),
  /** Até quantos dias à frente a agenda aparece na loja. */
  maxDaysAhead: z.coerce.number().int().min(1, 'Mínimo de 1 dia.').max(365, 'Máximo de 1 ano.'),
});
export type BookingSettingsInput = z.infer<typeof bookingSettingsSchema>;
export const updateBookingSettingsSchema = patchOf(bookingSettingsSchema);

/** Dias (ou horas de um dia) em que o dono da loja não quer atender. */
export const bookingBlockSchema = z
  .object({
    from: localDateSchema,
    /** Último dia do bloqueio (férias). Vazio = só `from`. */
    to: localDateSchema.optional(),
    /** Sem horário = o dia inteiro. */
    start: timeSchema.optional(),
    end: timeSchema.optional(),
    reason: z.string().trim().max(120).optional().nullable(),
  })
  .refine((b) => !b.to || b.to >= b.from, { message: 'O último dia precisa ser depois do primeiro.', path: ['to'] })
  .refine((b) => !b.to || (Date.parse(b.to) - Date.parse(b.from)) / 86400_000 < 366, { message: 'Bloqueie no máximo um ano de cada vez.', path: ['to'] })
  .refine((b) => !b.start === !b.end, { message: 'Informe o início e o fim, ou nenhum para o dia todo.', path: ['end'] })
  .refine((b) => !b.start || !b.end || timeToMinutes(b.start) < timeToMinutes(b.end), { message: 'O fim precisa ser depois do início.', path: ['end'] });
export type BookingBlockInput = z.infer<typeof bookingBlockSchema>;

export const bookingCustomerSchema = z.object({
  name: z.string().trim().min(3, 'Informe o nome completo.').max(120),
  email: z.string().trim().toLowerCase().email('E-mail inválido.'),
  phone: z.string().trim().min(10, 'Telefone inválido.').max(25),
});

/** Agendamento feito pelo cliente na loja. `time` é obrigatório nos serviços por horário. */
export const storeBookSchema = z.object({
  variantId: z.string().uuid(),
  date: localDateSchema,
  time: timeSchema.optional(),
  customer: bookingCustomerSchema,
  notes: z.string().trim().max(500).optional().nullable(),
  marketingOptIn: z.boolean().optional(),
});
export type StoreBookInput = z.infer<typeof storeBookSchema>;

/** Agendamento com pagamento na hora (Pix): o CPF do cliente vai na cobrança. */
export const storeBookPaySchema = storeBookSchema.extend({
  document: z.string().transform(onlyDigits).refine(isValidCpf, 'CPF inválido.'),
});
export type StoreBookPayInput = z.infer<typeof storeBookPaySchema>;

/** Agendamento lançado pelo dono da loja (cliente que ligou ou chamou no WhatsApp): e-mail opcional. */
export const panelBookSchema = storeBookSchema.omit({ customer: true, marketingOptIn: true }).extend({
  customer: bookingCustomerSchema.extend({ email: z.string().trim().toLowerCase().email('E-mail inválido.').optional().or(z.literal('')) }),
});
export type PanelBookInput = z.infer<typeof panelBookSchema>;

export const bookingAvailabilityQuerySchema = z.object({ variantId: z.string().uuid(), month: monthSchema });
export const bookingSlotsQuerySchema = z.object({ variantId: z.string().uuid(), date: localDateSchema });
export const bookingCalendarQuerySchema = z.object({ month: monthSchema });
export const bookingDayQuerySchema = z.object({ date: localDateSchema });

export const listBookingsQuerySchema = z.object({
  from: localDateSchema.optional(),
  to: localDateSchema.optional(),
  status: z.enum(BOOKING_STATUSES).optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Depois do atendimento o dono marca se o cliente veio. */
export const bookingStatusSchema = z.object({ status: z.enum(['CONFIRMED', 'COMPLETED', 'NO_SHOW']) });

export const cancelBookingSchema = z.object({ reason: z.string().trim().max(300).optional().nullable() });

/** O dono recusa um pedido de agendamento (o cliente recebe o motivo por e-mail). */
export const declineBookingSchema = cancelBookingSchema;

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  AWAITING_PAYMENT: 'Aguardando pagamento',
  PENDING: 'Aguardando confirmação',
  CONFIRMED: 'Confirmado',
  COMPLETED: 'Atendido',
  NO_SHOW: 'Não compareceu',
  CANCELED: 'Cancelado',
};

// ---------- Assinatura com agendamento ----------

/**
 * Serviço vendido por assinatura e marcado pela agenda: o dono define quantos atendimentos cabem
 * em cada semana ou mês do plano, e o cliente escolhe na compra se quer horário fixo (repete
 * sozinho) ou se marca cada atendimento quando quiser, usando os atendimentos do período.
 */
export const BOOKING_QUOTA_PERIODS = ['WEEK', 'MONTH'] as const;
export type BookingQuotaPeriod = (typeof BOOKING_QUOTA_PERIODS)[number];

/** FIXED: sempre no mesmo dia da semana (ou do mês) e horário. FLEXIBLE: o cliente marca cada vez. */
export const BOOKING_STYLES = ['FIXED', 'FLEXIBLE'] as const;
export type BookingStyle = (typeof BOOKING_STYLES)[number];

export const BOOKING_QUOTA_PERIOD_LABEL: Record<BookingQuotaPeriod, { per: string; noun: string }> = {
  WEEK: { per: 'por semana', noun: 'semana' },
  MONTH: { per: 'por mês', noun: 'mês' },
};

export const BOOKING_STYLE_LABEL: Record<BookingStyle, string> = {
  FIXED: 'Sempre no mesmo dia e horário',
  FLEXIBLE: 'Escolher o dia de cada atendimento',
};

/** "2 atendimentos por semana". */
export function quotaText(quota: number, period: BookingQuotaPeriod): string {
  return `${quota} ${quota === 1 ? 'atendimento' : 'atendimentos'} ${BOOKING_QUOTA_PERIOD_LABEL[period].per}`;
}

/**
 * Horário escolhido pelo cliente na compra. No horário fixo, cada escolha é o primeiro atendimento
 * de uma série que se repete toda semana (no mesmo dia da semana) ou todo mês (no mesmo dia do mês).
 */
export const bookingPickSchema = z.object({ date: localDateSchema, time: timeSchema.optional() });
export type BookingPick = z.infer<typeof bookingPickSchema>;

export const subscriptionScheduleSchema = z
  .object({
    style: z.enum(BOOKING_STYLES),
    picks: z.array(bookingPickSchema).max(31).default([]),
  })
  .refine((s) => s.style === 'FLEXIBLE' || s.picks.length > 0, { message: 'Escolha os dias e horários fixos.', path: ['picks'] })
  .refine((s) => new Set(s.picks.map((p) => `${p.date} ${p.time ?? ''}`)).size === s.picks.length, {
    message: 'Escolha horários diferentes.',
    path: ['picks'],
  });
export type SubscriptionScheduleInput = z.infer<typeof subscriptionScheduleSchema>;

/** Atendimento marcado pelo assinante (na área do cliente), usando um dos atendimentos do período. */
export const subscriberBookSchema = z.object({
  date: localDateSchema,
  time: timeSchema.optional(),
  notes: z.string().trim().max(500).optional().nullable(),
});
export type SubscriberBookInput = z.infer<typeof subscriberBookSchema>;
